import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import { BorderedBlock } from '@shared/ui/common/BorderedBlock';
import { Supplier, SupplierProduct } from '@domain/suppliers/models/Supplier';
import { ProductSelectBottomSheet } from '@features/products';
import { BottomSheetSize, useBottomSheet } from '@shared/shell/bottomsheet/useBottomSheet';
import { Product } from '@domain/product/models/Product';
import LinkProductsToSupplier from '../links/LinkProductsToSupplier';
import SupplierProductRow, { getSupplierProductDisplayName } from './SupplierProductRow';
import { useSupplier } from '../../hooks/useSupplier';
import { useToast } from '@shared/hooks/useToast';
import { Icon } from '@shared/icons';

export interface SupplierLinkedProductsTableProps {
  supplierProducts: SupplierProduct[];
  supplier: Supplier;
}

const SupplierLinkedProductsTable: React.FC<SupplierLinkedProductsTableProps> = ({
  supplierProducts,
  supplier,
}) => {
  const theme = useTheme();
  const { openBottomSheet, closeBottomSheet } = useBottomSheet();
  const { unlinkProducts, isUnlinkingProduct } = useSupplier(supplier.id);
  const { success, error: showError } = useToast();

  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [unlinkDialogOpen, setUnlinkDialogOpen] = useState(false);

  const sortedLinks = useMemo(
    () =>
      [...supplierProducts].sort((a, b) => a.product?.title?.localeCompare(b.product?.title ?? '') ?? 0),
    [supplierProducts]
  );

  const productIds = useMemo(() => sortedLinks.map(l => l.product?.id ?? ''), [sortedLinks]);

  useEffect(() => {
    const valid = new Set(productIds);
    setSelectedIds(prev => new Set([...prev].filter(id => valid.has(id))));
  }, [productIds]);

  const allSelected = productIds.length > 0 && productIds.every(id => selectedIds.has(id));
  const someSelected = selectedIds.size > 0 && !allSelected;

  const toggleSelect = useCallback((productId: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(productId)) next.delete(productId);
      else next.add(productId);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(productIds));
    }
  }, [allSelected, productIds]);

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  const selectProductsToLink = () => {
    openBottomSheet({
      title: 'Link Product',
      component: (
        <ProductSelectBottomSheet
          excludeProductIds={new Set(productIds)}
          onClose={closeBottomSheet}
          onFinish={selections => {
            const products = selections.map(s => s.product);
            if (!supplier || products.length === 0) return;
            closeBottomSheet();
            handleLinkProductFinish(products);
          }}
        />
      ),
      size: BottomSheetSize.MD,
    });
  };

  const handleLinkProductFinish = (products: Product[]) => {
    closeBottomSheet();
    openBottomSheet({
      title: 'Link Product',
      component: (
        <LinkProductsToSupplier initialSupplier={supplier} initialProducts={products} onClose={closeBottomSheet} />
      ),
      size: BottomSheetSize.MD,
    });
  };

  const handleConfirmBulkUnlink = async () => {
    const ids = [...selectedIds];
    if (ids.length === 0) return;
    try {
      await unlinkProducts({ supplierId: supplier.id, productIds: ids });
      setUnlinkDialogOpen(false);
      clearSelection();
      const n = ids.length;
      const firstLink = n === 1 ? sortedLinks.find(l => l.product?.id === ids[0]) : undefined;
      success(
        n === 1 && firstLink
          ? `Unlinked “${getSupplierProductDisplayName(firstLink)}” from ${supplier.name}`
          : `Unlinked ${n} products from ${supplier.name}`
      );
    } catch (e) {
      showError(e instanceof Error ? e.message : 'Failed to unlink products');
    }
  };

  const selectionCount = selectedIds.size;
  const singleSelectedLink =
    selectionCount === 1 ? sortedLinks.find(l => l.product?.id === [...selectedIds][0]) : undefined;

  return (
    <BorderedBlock
      sx={{ p: 0 }}
      header={
        <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
          <Typography variant="subtitle2">Products</Typography>
          <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" justifyContent="flex-end">
            {selectionCount > 0 ? (
              <>
                <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
                  {selectionCount} selected
                </Typography>
             
                <Button
                  variant="text"
                  color="error"
                  startIcon={<Icon icon="LinkBroken" />}
                  onClick={() => setUnlinkDialogOpen(true)}
                >
                  Unlink
                </Button>
              </>
            ) : (
              <Button startIcon={<Icon icon="Link" />} variant="text" color="primary" onClick={selectProductsToLink}>
                Link products
              </Button>
            )}
          </Stack>
        </Stack>
      }
    >
      {supplierProducts.length === 0 ? (
        <Box sx={{ p: 4 }}>
          <Typography variant="subtitle2" color="text.secondary">
            No products linked yet. Link a product or add suppliers from a product page.
          </Typography>
        </Box>
      ) : (
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 720 }}>
            <TableHead>
              <TableRow sx={{ bgcolor: theme.palette.background.default }}>
                <TableCell padding="checkbox" sx={{ py: 1.5 }}>
                  <Checkbox
                    checked={allSelected}
                    indeterminate={someSelected}
                    onChange={toggleSelectAll}
                    inputProps={{ 'aria-label': 'Select all linked products' }}
                  />
                </TableCell>
                <TableCell sx={{ py: 1.5 }}>Product</TableCell>
                <TableCell align="right" sx={{ py: 1.5, minWidth: 120, whiteSpace: 'nowrap' }}>
                  MOQ
                </TableCell>
                <TableCell align="right" sx={{ py: 1.5, minWidth: 100, whiteSpace: 'nowrap' }}>
                  In multiples of
                </TableCell>
                <TableCell align="right" sx={{ py: 1.5, whiteSpace: 'nowrap' }}>
                  Purchase price
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedLinks.map(linked => (
                <SupplierProductRow
                  key={linked.id}
                  supplierProduct={linked}
                  selected={selectedIds.has(linked.product?.id ?? '')}
                  onToggleSelect={toggleSelect}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog
        open={unlinkDialogOpen}
        onClose={() => !isUnlinkingProduct && setUnlinkDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Unlink products</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            {selectionCount === 1 ? (
              <>
                Are you sure you want to unlink
                {singleSelectedLink ? (
                  <>
                    {' '}
                    &quot;{getSupplierProductDisplayName(singleSelectedLink)}&quot;
                  </>
                ) : (
                  ' this product'
                )}{' '}
                from this supplier? This action cannot be undone.
              </>
            ) : (
              <>
                Are you sure you want to unlink {selectionCount} products from this supplier? This action cannot be
                undone.
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnlinkDialogOpen(false)} disabled={isUnlinkingProduct}>
            Cancel
          </Button>
          <Button
            onClick={() => void handleConfirmBulkUnlink()}
            color="error"
            variant="contained"
            disabled={isUnlinkingProduct}
          >
            {isUnlinkingProduct ? 'Unlinking…' : 'Unlink'}
          </Button>
        </DialogActions>
      </Dialog>
    </BorderedBlock>
  );
};

export default SupplierLinkedProductsTable;
